/**
 * #5250 PREVIEWSIGNIN, the browser's half. On a PR preview host, sign-in goes
 * to the preview's own start route, which sets a nonce cookie and sends the
 * browser to the staging slot (the one host Google and Entra return to)
 * carrying `?handoff=<this preview>&nonce_hash=<its hash>`. On the slot,
 * /login and /auth/return carry both through OAuth, then show one confirm
 * button naming the preview and the signed-in email; pressing it is the only
 * thing that asks the server for a code and sends the browser to the
 * preview's claim (round 2, PR 5289 review). Pure functions of the hostname
 * and query string, so the same build behaves right on production, the slot,
 * a preview and localhost.
 */
import {
  HANDOFF_PARAM,
  NONCE_HASH_PARAM,
  PREVIEW_HANDOFF_PATH,
  STAGING_SLOT_HOST,
  isNonceHash,
  isPreviewHost,
  previewClaimUrl,
} from "@shared/preview-signin";
import { returnToPath } from "./auth-return";

function currentHostname(): string {
  return typeof window === "undefined" ? "" : window.location.hostname;
}

/** This page is served on a preview host, so its own OAuth would land on the slot. */
export function onPreviewHost(hostname: string = currentHostname()): boolean {
  return isPreviewHost(hostname);
}

/** A sign-in the slot is to hand on: the preview host, and the hash of the nonce that preview set in this browser. */
export interface PendingHandoff {
  host: string;
  nonceHash: string;
}

/** On the slot only: the hand-off this sign-in is for, from ?handoff= and ?nonce_hash=. Null anywhere else, or when either is not well formed. */
export function handoffTarget(search: string, hostname: string = currentHostname()): PendingHandoff | null {
  if (hostname !== STAGING_SLOT_HOST) return null;
  const params = new URLSearchParams(search);
  const host = params.get(HANDOFF_PARAM);
  const nonceHash = params.get(NONCE_HASH_PARAM);
  return isPreviewHost(host) && isNonceHash(nonceHash) ? { host, nonceHash } : null;
}

/** `route?returnTo=...`, plus the hand-off when there is one. */
export function withHandoff(route: string, returnTo: string, handoff: PendingHandoff | null): string {
  const path = returnToPath(route, returnTo);
  if (!handoff) return path;
  return `${path}&${HANDOFF_PARAM}=${encodeURIComponent(handoff.host)}&${NONCE_HASH_PARAM}=${handoff.nonceHash}`;
}

/**
 * Ask the slot for a code for this hand-off and say where the browser goes
 * next: the preview's claim, or /auth-error with the refusal's code. Called
 * from the confirm button only.
 */
export async function previewHandoffDestination(
  handoff: PendingHandoff,
  returnTo: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  try {
    const res = await fetchImpl(PREVIEW_HANDOFF_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ host: handoff.host, nonce_hash: handoff.nonceHash }),
    });
    const body = (await res.json().catch(() => null)) as { code?: unknown; error?: unknown } | null;
    if (res.ok && typeof body?.code === "string") return previewClaimUrl(handoff.host, body.code, returnTo);
    const error = typeof body?.error === "string" ? body.error : "handoff_failed";
    return `/auth-error?error=${encodeURIComponent(error)}`;
  } catch {
    return "/auth-error?error=handoff_failed";
  }
}
